import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TiposProductoComponent } from '../components/tipos-productos/tipos-productos.component';
import { ListaProductosComponent } from '../components/lista-productos/lista-productos.component';
import { GenericFilterComponent } from '../../../common/components/generic-filter/generic-filter.component';
import { ProductosComercializadoresService } from '../services/productos-comercializadores.service';
import { ProductUtilsService } from '../services/product-util.service';
import { PresentacionProductoComercializador } from '../interfaces/producto-comercializador.interface';
import { ProductTypeResponseInterface } from 'src/interfaces/product-type-response.interface';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { RegistroService } from '../../../registro.service';

/**
 * Componente principal para registro de productos comercializadores
 *
 * Permite seleccionar tipos de productos y asociar productos específicos
 * que comercializa empresas comercializadoras.
 *
 * @since 1.0.0
 */
@Component({
    selector: 'app-registro-productos-comercializadores',
    standalone: true,
    imports: [
        CommonModule,
        ListaProductosComponent,
        TiposProductoComponent,
        GenericFilterComponent,
    ],
    templateUrl: './registro-productos-comercializadores.component.html',
    styleUrls: ['./registro-productos-comercializadores.component.css'],
})
export class RegistroProductosComercializadoresComponent implements OnInit {
    categoriasProductos: ProductTypeResponseInterface[] = [];
    listaProductosCompleta: PresentacionProductoComercializador[] = [];
    categoriasSeleccionadas: any[] = [];
    productosFiltrados: any[] = [];
    productosSeleccionados: any[] = [];
    loading = false;
    error = '';

    constructor(
        private readonly presentacionService: ProductosComercializadoresService,
        private readonly productUtils: ProductUtilsService,
        private readonly router: Router,
        private readonly registroService: RegistroService
    ) {}

    /**
     * Inicializa el componente cargando las categorías
     */
    ngOnInit(): void {
        this.cargarCategorias();
    }

    /**
     * Carga las categorías de productos desde el servicio
     * @private
     */
    private readonly TIPO_ACONDICIONADOR_ENMIENDA_ID = 5;

    private async cargarCategorias(): Promise<void> {
        try {
            const idAgenteVigilado = this.registroService.empresaSelected;

            if (!idAgenteVigilado) {
                this.categoriasProductos = [];
                this.error = 'No hay una empresa seleccionada';
                return;
            }

            // HU147: los tipos de producto disponibles dependen del
            // tipo_producto_que_importa de la empresa seleccionada.
            const categorias =
                await this.presentacionService.getTiposDisponibles(
                    idAgenteVigilado
                );
            this.categoriasProductos = categorias.filter(
                (categoria) =>
                    categoria.id !== this.TIPO_ACONDICIONADOR_ENMIENDA_ID
            );
        } catch (err) {
            console.error('Error al cargar tipos de producto:', err);
            this.error = 'Error al cargar las categorías';
        }
    }

    /**
     * Carga productos según categorías seleccionadas
     */
    async cargarProductos(): Promise<void> {
        this.loading = true;
        this.error = '';

        try {
            if (this.categoriasSeleccionadas.length === 0) {
                this.productosFiltrados = [];
                return;
            }

            const tiposIds = this.categoriasSeleccionadas.map(
                (categoria) => categoria.id
            );

            this.listaProductosCompleta =
                await this.presentacionService.getPresentacionesPorTipos(
                    tiposIds,
                    this.registroService.empresaSelected
                );

            this.filtrarProductosDisponibles();
        } catch (error) {
            this.error = 'Error al cargar los productos desde el servidor';
            console.error('Error:', error);
        } finally {
            this.loading = false;
        }
    }

    /**
     * Obtiene nombre legible de tipo de producto
     * @param tipoId - ID del tipo de producto
     * @returns Nombre del tipo o 'Desconocido'
     */
    obtenerNombreTipo(tipoId: number): string {
        const tipoEncontrado = this.categoriasProductos.find(
            (categoria) => categoria.id === tipoId
        );
        return tipoEncontrado ? tipoEncontrado.nombre : 'Desconocido';
    }

    /**
     * Alterna selección de categoría
     * @param categoria - Categoría a seleccionar/deseleccionar
     */
    alternarSeleccionCategoria(categoria: any): void {
        const indiceCategoria = this.categoriasSeleccionadas.indexOf(categoria);
        if (indiceCategoria === -1) {
            this.categoriasSeleccionadas.push(categoria);
        } else {
            this.categoriasSeleccionadas.splice(indiceCategoria, 1);
        }
        this.cargarProductos();
    }

    /**
     * Filtra productos disponibles basado en categorías seleccionadas
     * @private
     */
    filtrarProductosDisponibles(): void {
        if (this.categoriasSeleccionadas.length === 0) {
            this.productosFiltrados = [];
            return;
        }

        const idsCategoriasSeleccionadas = new Set(
            this.categoriasSeleccionadas.map((categoria) => categoria.id)
        );

        this.productosFiltrados = this.listaProductosCompleta
            .filter(
                (producto) =>
                    idsCategoriasSeleccionadas.has(
                        producto.productoComercializador.tipoProductoId
                    ) &&
                    !this.productosSeleccionados.some(
                        (seleccionado) => seleccionado.id === producto.id
                    )
            )
            .map((producto) => ({
                ...producto,
                tipoId: producto.productoComercializador.tipoProductoId,
                descripcion:
                    this.productUtils.generarDescripcionProducto(producto),
            }));
    }

    /**
     * Agrega producto a la lista de seleccionados
     * @param producto - Producto a agregar
     */
    agregarProductoASeleccion(
        producto: PresentacionProductoComercializador
    ): void {
        this.productosSeleccionados.push({
            ...producto,
            tipoId: producto.productoComercializador.tipoProductoId,
            descripcion: this.productUtils.generarDescripcionProducto(producto),
        });
        this.filtrarProductosDisponibles();
    }

    /**
     * Remueve producto de la lista de seleccionados
     * @param producto - Producto a remover
     */
    quitarProductoDeSeleccion(
        producto: PresentacionProductoComercializador
    ): void {
        this.productosSeleccionados = this.productosSeleccionados.filter(
            (productoSeleccionado) => productoSeleccionado.id !== producto.id
        );
        this.filtrarProductosDisponibles();
    }

    /** Obtiene información adicional del producto (usa ProductUtilsService) */
    obtenerInformacionProducto(
        producto: PresentacionProductoComercializador
    ): string {
        return this.productUtils.obtenerInformacionProducto(producto);
    }

    /** Obtiene el color asociado a un tipo de producto */
    obtenerColorPorTipo(tipoId: number): string {
        return this.productUtils.obtenerColorPorTipo(tipoId);
    }

    /**
     * Guarda la selección actual de productos
     */
    guardarSeleccion(): void {
        const datosParaGuardar = this.productUtils.prepararDatosParaGuardar(
            this.productosSeleccionados
        );

        this.presentacionService
            .postPresentacionesSeleccionadas(datosParaGuardar)
            .then(() =>
                Swal.fire('', 'Productos asociados correctamente', 'success')
            )
            .then((result) => {
                if (result?.isConfirmed) {
                    this.router.navigate([
                        '/consultar-productos-comercializadores',
                    ]);
                }
            })
            .catch((error) => {
                console.error(
                    'Error al guardar las presentaciones seleccionadas:',
                    error
                );
                Swal.fire(
                    '',
                    error?.error?.message ||
                        'Error al guardar las presentaciones seleccionadas',
                    'error'
                );
            });
    }
}
